import { Link, NavLink, useLocation, useSearchParams } from "react-router-dom";
import { Coin3D, Wordmark } from "@/components/brand/Logo";
import { useFeatures } from "@/hooks/use-features";
import { WS_HOME, labelOf, navFor, utilityNav, workspaceLinks, workspaceOf } from "@/components/layout/nav";
import { useAuth } from "@/context/AuthContext";
import { FOYER_TABS } from "@/lib/foyer-tabs";
import { cn } from "@/lib/utils";

const FoyerSub = () => {
  const [params] = useSearchParams();
  const tab = params.get("tab");
  const current = FOYER_TABS.some((t) => t.value === tab) ? tab : "equipements";
  return (
    <ul className="ml-6 mt-1 flex flex-col gap-0.5 border-l border-border pl-3" data-testid="sidebar-foyer-sub">
      {FOYER_TABS.map((t) => (
        <li key={t.value}>
          <Link to={`/foyer?tab=${t.value}`} data-testid={`sidebar-foyer-${t.value}`} aria-current={current === t.value ? "page" : undefined}
            className={cn("flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition-colors", current === t.value ? "bg-secondary font-semibold text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const Item = ({ item, active }) => {
  const { pots_enabled } = useFeatures();
  const soon = item.soon === "account" || (item.soon === "pots" && !pots_enabled);
  return (
  <NavLink
    to={item.to}
    end={item.end}
    data-testid={`sidebar-nav-${item.key}`}
    className={({ isActive }) =>
      cn(
        "group flex items-center gap-3 rounded-xl px-2 py-2 text-sm font-medium transition-colors duration-200",
        `tone-${item.tone}`,
        isActive || active ? "tone-tile text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground",
      )
    }
  >
    <span className="tone-chip flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-transform duration-200 group-hover:scale-110">
      <item.icon className="h-4 w-4" strokeWidth={2} />
    </span>
    <span className={soon ? "min-w-0 flex-1 leading-tight" : "truncate"}>{labelOf(item)}</span>
    {soon && <span className="soon-pill shrink-0" data-testid={`sidebar-soon-${item.key}`}>Bientôt</span>}
  </NavLink>
  );
};

export const Sidebar = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const ws = workspaceOf(user, pathname);
  const spaces = workspaceLinks(user);
  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col overflow-y-auto border-r border-border bg-card/60 px-4 py-6 md:flex" data-testid="desktop-sidebar" data-workspace={ws}>
      <Link to={WS_HOME[ws]} aria-label="Accueil" className="flex items-center gap-3 rounded-xl px-2 transition-opacity hover:opacity-80" data-testid="sidebar-logo-home"><Coin3D size={48} /><Wordmark className="text-xl" /></Link>
      <nav className="mt-10 flex flex-col gap-1">
        {navFor(user, pathname).map((i) => (
          <div key={i.key}>
            <Item item={i} />
            {i.key === "foyer" && pathname.startsWith("/foyer") && <FoyerSub />}
          </div>
        ))}
      </nav>
      {spaces.length > 1 && (
        <>
          <div className="mt-8 eyebrow px-3">Mes espaces</div>
          <nav className="mt-2 flex flex-col gap-1" data-testid="workspace-switcher">
            {spaces.map((i) => <Item key={i.key} item={{ ...i, end: true }} active={i.ws === ws} />)}
          </nav>
        </>
      )}
      <div className="mt-8 eyebrow px-3">Compte</div>
      <nav className="mt-2 flex flex-col gap-1">
        {utilityNav(user).map((i) => <Item key={i.key} item={i} />)}
      </nav>
      <p className="mt-auto px-3 pt-6 text-xs text-muted-foreground">My cash. My home. My life.</p>
    </aside>
  );
};
